import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  invoicingApi,
  type ApproveVoidRequest,
  type CreateSalesInvoiceRequest,
  type CreateSupplierInvoiceRequest,
  type GenerateSalesInvoiceFromOrderRequest,
  type PrintReceiptRequest,
  type RejectVoidRequest,
  type RequestVoidRequest,
  type SupplierInvoicePaymentRequest,
  type SupplierInvoiceReasonRequest,
} from "../services/invoicingApi";

export const useCreateSupplierInvoice = () => useMutation({
  mutationFn: (payload: CreateSupplierInvoiceRequest) => invoicingApi.createSupplierInvoice(payload),
  onSuccess: () => queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] }),
});

export const useSupplierInvoices = (pageNumber: number, pageSize: number, status?: number, overdue?: boolean, search?: string) => {
  return useQuery({
    queryKey: ["supplier-invoices", pageNumber, pageSize, status, overdue, search],
    queryFn: () => invoicingApi.getAllSupplierInvoices(pageNumber, pageSize, status, overdue, search),
  });
};

export const useSupplierInvoiceById = (invoiceId?: string) => {
  return useQuery({
    queryKey: ["supplier-invoices", "detail", invoiceId],
    queryFn: () => invoicingApi.getSupplierInvoiceById(invoiceId as string),
    enabled: !!invoiceId,
  });
};

export const useViewSupplierInvoicePdf = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.viewSupplierInvoicePdf(invoiceId),
  });
};

export const useDownloadSupplierInvoicePdf = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.downloadSupplierInvoicePdf(invoiceId),
  });
};

export const useSupplierInvoiceByNumber = (invoiceNumber?: string) => {
  return useQuery({
    queryKey: ["supplier-invoices", "by-number", invoiceNumber],
    queryFn: () => invoicingApi.getSupplierInvoiceByNumber(invoiceNumber as string),
    enabled: !!invoiceNumber,
  });
};

export const useSupplierInvoicesBySupplierId = (supplierId?: string) => {
  return useQuery({
    queryKey: ["supplier-invoices", "supplier", supplierId],
    queryFn: () => invoicingApi.getSupplierInvoicesBySupplierId(supplierId as string),
    enabled: !!supplierId,
  });
};

export const useSupplierInvoicesByStatus = (status?: number) => {
  return useQuery({
    queryKey: ["supplier-invoices", "status", status],
    queryFn: () => invoicingApi.getSupplierInvoicesByStatus(status as number),
    enabled: typeof status === "number",
  });
};

export const useOverdueSupplierInvoices = (enabled: boolean) => {
  return useQuery({
    queryKey: ["supplier-invoices", "overdue"],
    queryFn: invoicingApi.getOverdueSupplierInvoices,
    enabled,
  });
};

export const useApproveSupplierInvoice = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.approveSupplierInvoice(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
    },
  });
};

export const useGenerateSupplierInvoiceFromBatch = () => {
  return useMutation({
    mutationFn: (batchId: string) => invoicingApi.generateSupplierInvoiceFromBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices", "overdue"] });
    },
  });
};

export const useGenerateSupplierInvoicesFromBatches = () => {
  return useMutation({
    mutationFn: (batchIds: string[]) =>
      invoicingApi.generateSupplierInvoicesFromBatches(batchIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices", "overdue"] });
    },
  });
};

export const useRecordSupplierInvoicePayment = () => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string;
      payload: SupplierInvoicePaymentRequest;
    }) => invoicingApi.recordSupplierInvoicePayment(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
    },
  });
};

export const useDisputeSupplierInvoice = () => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string;
      payload: SupplierInvoiceReasonRequest;
    }) => invoicingApi.disputeSupplierInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
    },
  });
};

export const useVoidSupplierInvoice = () => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string;
      payload: SupplierInvoiceReasonRequest;
    }) => invoicingApi.voidSupplierInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
    },
  });
};

export const useSalesInvoices = (pageNumber: number, pageSize: number, status?: number, overdue?: boolean, search?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", pageNumber, pageSize, status, overdue, search],
    queryFn: () => invoicingApi.getAllSalesInvoices(pageNumber, pageSize, status, overdue, search),
  });
};

export const useSalesInvoiceById = (invoiceId?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", "detail", invoiceId],
    queryFn: () => invoicingApi.getSalesInvoiceById(invoiceId as string),
    enabled: !!invoiceId,
  });
};

export const useViewSalesInvoicePdf = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.viewSalesInvoicePdf(invoiceId),
  });
};

export const useDownloadSalesInvoicePdf = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.downloadSalesInvoicePdf(invoiceId),
  });
};

export const useSalesInvoiceByNumber = (invoiceNumber?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", "by-number", invoiceNumber],
    queryFn: () => invoicingApi.getSalesInvoiceByNumber(invoiceNumber as string),
    enabled: !!invoiceNumber,
  });
};

export const useSalesInvoicesByCustomerId = (customerId?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", "customer", customerId],
    queryFn: () => invoicingApi.getSalesInvoicesByCustomerId(customerId as string),
    enabled: !!customerId,
  });
};

export const useSalesInvoicesByOrderId = (orderId?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", "order", orderId],
    queryFn: () => invoicingApi.getSalesInvoicesByOrderId(orderId as string),
    enabled: !!orderId,
  });
};

export const useSalesInvoicesByStatus = (status?: number) => {
  return useQuery({
    queryKey: ["sales-invoices", "status", status],
    queryFn: () => invoicingApi.getSalesInvoicesByStatus(status as number),
    enabled: typeof status === "number",
  });
};

export const useOverdueSalesInvoices = (enabled: boolean) => {
  return useQuery({
    queryKey: ["sales-invoices", "overdue"],
    queryFn: invoicingApi.getOverdueSalesInvoices,
    enabled,
  });
};

export const useCreateSalesInvoice = () => {
  return useMutation({
    mutationFn: (payload: CreateSalesInvoiceRequest) =>
      invoicingApi.createSalesInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
    },
  });
};

export const useGenerateSalesInvoiceFromOrder = () => {
  return useMutation({
    mutationFn: (payload: GenerateSalesInvoiceFromOrderRequest) =>
      invoicingApi.generateSalesInvoiceFromOrder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const useIssueSalesInvoice = () => {
  return useMutation({
    mutationFn: (invoiceId: string) => invoicingApi.issueSalesInvoice(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
    },
  });
};

export const useVoidSalesInvoice = () => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string;
      payload: RequestVoidRequest;
    }) => invoicingApi.requestVoidSalesInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
    },
  });
};

export const useRequestVoidSalesInvoice = () => {
  return useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: RequestVoidRequest }) =>
      invoicingApi.requestVoidSalesInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["sales-invoices", "pending-void"] });
    },
  });
};

export const usePendingVoidSalesInvoices = () => {
  return useQuery({
    queryKey: ["sales-invoices", "pending-void"],
    queryFn: invoicingApi.getPendingVoidSalesInvoices,
  });
};

export const useApproveVoidSalesInvoice = () => {
  return useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: ApproveVoidRequest }) =>
      invoicingApi.approveVoidSalesInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["sales-invoices", "pending-void"] });
      queryClient.invalidateQueries({ queryKey: ["credit-notes"] });
    },
  });
};

export const useRejectVoidSalesInvoice = () => {
  return useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: RejectVoidRequest }) =>
      invoicingApi.rejectVoidSalesInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["sales-invoices", "pending-void"] });
    },
  });
};

export const useCreditNotes = () => {
  return useQuery({
    queryKey: ["credit-notes"],
    queryFn: invoicingApi.getAllCreditNotes,
  });
};

export const useCreditNoteById = (id?: string) => {
  return useQuery({
    queryKey: ["credit-notes", "detail", id],
    queryFn: () => invoicingApi.getCreditNoteById(id as string),
    enabled: !!id,
  });
};

export const useCreditNoteByInvoiceId = (invoiceId?: string) => {
  return useQuery({
    queryKey: ["credit-notes", "invoice", invoiceId],
    queryFn: () => invoicingApi.getCreditNoteByInvoiceId(invoiceId as string),
    enabled: !!invoiceId,
  });
};

export const usePrintInvoiceReceipt = () => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      payload,
    }: {
      invoiceId: string;
      payload: PrintReceiptRequest;
    }) => invoicingApi.printReceipt(invoiceId, payload),
  });
};

export const useInvoiceReceiptHistory = (invoiceId?: string) => {
  return useQuery({
    queryKey: ["sales-invoices", "receipt-history", invoiceId],
    queryFn: () => invoicingApi.getReceiptHistory(invoiceId as string),
    enabled: !!invoiceId,
  });
};
